import type React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import DatabaseIcon from '@atlaskit/icon/core/database';

import { Ati } from '@atlassian/ari';

import { expValEquals } from '@confluence/feature-experiments';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.page.database',
		defaultMessage: 'Database',
		description: 'title indicating that this option will create a database',
	},
});

export function useDatabaseItem({
	parentId,
	source,
	spaceKey,
	onClick,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { isRovoLLMEnabled } = useSessionData();

	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'create-database-popover-item',
	});

	const createContentOptions = {
		contentType: 'database',
		parentPageId: parentId,
		withFallback: true,
		source,
		spaceKey,
	} as const;

	const createHref = buildCreationLink(createContentOptions);
	const hybridPageLinkClick = useHybridLinkClick(createHref);

	const { openModal } = useSmartCreationModalActions();
	const { push } = useRouteActions();

	const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		onClick('createDatabasePopoverItem');

		// When cwr_blank_object_experiment is variant3, we
		// open CWR modal and pass in the normal creation flow as a callback
		// to fire if the user closes the modal. If the user goes through the CWR flow,
		// CWR creates the new content.
		if (isRovoLLMEnabled && expValEquals('cwr_blank_object_experiment', 'cohort', 'variant3')) {
			e.preventDefault();
			openModal({
				entryPoint: `${source}-cwrBlankObject`,
				spaceKey,
				initialOutcomeType: 'database',
				onCloseCallback: () => push(createHref),
			});
			return;
		}

		hybridPageLinkClick(e);
	};

	return {
		ati: Ati.parse('ati:cloud:confluence:database'),
		children: formatMessage(messages.title),
		onClick: enforceStorageLimit(handleClick),
		elemBefore: DatabaseIcon,
		...(!shouldBlockCreate && { href: createHref }),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}
